import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Participant, CriteriaGroup } from '@/types/evaluation.types';
import { ParticipantType, InvitationStatus } from '@/types/evaluation.types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { X, UserPlus, AlertCircle, Info } from 'lucide-react';

interface AddParticipantFormProps {
  existingParticipants: Participant[];
  criteriaGroups: CriteriaGroup[];
  invitationExpirationDays: number;
  onAdd: (participant: Omit<Participant, 'id'>) => void;
  onCancel: () => void;
  generateId: () => string;
}

export function AddParticipantForm({
  existingParticipants,
  criteriaGroups,
  invitationExpirationDays,
  onAdd,
  onCancel,
}: AddParticipantFormProps) {
  const { t } = useTranslation(['participants', 'common']);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [participantType, setParticipantType] = useState<ParticipantType>(ParticipantType.Evaluator);

  // Validation errors
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check for duplicate email
  const isDuplicateEmail = (email: string): boolean => {
    return existingParticipants.some(
      (p) => p.email.toLowerCase() === email.toLowerCase()
    );
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = t('participants:addForm.validation.nameRequired');
    }

    // Email validation
    if (!email.trim()) {
      newErrors.email = t('participants:addForm.validation.emailRequired');
    } else if (!isValidEmail(email)) {
      newErrors.email = t('participants:addForm.validation.emailInvalid');
    } else if (isDuplicateEmail(email)) {
      newErrors.email = t('participants:addForm.validation.emailDuplicate');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Calculate invitation expiration date
    const invitedDate = new Date();
    const expirationDate = new Date();
    expirationDate.setDate(invitedDate.getDate() + invitationExpirationDays);

    // Create new participant (without id - parent will add it)
    const newParticipant: Omit<Participant, 'id'> = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim() || undefined,
      participantType,
      criteriaGroupAssignments: null, // Default: can score all groups
      invitedDate,
      invitationExpirationDate: expirationDate,
      lastActivityDate: null,
      invitationStatus: InvitationStatus.Pending,
    };

    onAdd(newParticipant);

    // Reset form
    setName('');
    setEmail('');
    setPhone('');
    setParticipantType(ParticipantType.Evaluator);
    setErrors({});
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('participants:addForm.title')}</CardTitle>
            <CardDescription className="mt-1.5">
              {t('participants:subtitle')}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel} aria-label={t('common:actions.cancel')}>
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mock invitation notice */}
          <Alert>
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{t('participants:addForm.mockNotice')}</AlertDescription>
          </Alert>

          {/* Name field */}
          <div className="space-y-2">
            <Label htmlFor="participant-name">
              {t('participants:addForm.fields.name.label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="participant-name"
              type="text"
              placeholder={t('participants:addForm.fields.name.placeholder')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
              className={errors.name ? 'border-destructive' : ''}
              autoFocus
            />
            {errors.name && (
              <p id="name-error" className="text-sm text-destructive" role="alert">
                {errors.name}
              </p>
            )}
          </div>

          {/* Email field */}
          <div className="space-y-2">
            <Label htmlFor="participant-email">
              {t('participants:addForm.fields.email.label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="participant-email"
              type="email"
              placeholder={t('participants:addForm.fields.email.placeholder')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p id="email-error" className="text-sm text-destructive" role="alert">
                {errors.email}
              </p>
            )}
          </div>

          {/* Phone field */}
          <div className="space-y-2">
            <Label htmlFor="participant-phone">
              {t('participants:addForm.fields.phone.label')}
            </Label>
            <Input
              id="participant-phone"
              type="tel"
              placeholder={t('participants:addForm.fields.phone.placeholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {/* Participant Type */}
          <div className="space-y-3">
            <Label>{t('participants:addForm.fields.type.label')} <span className="text-destructive">*</span></Label>
            <RadioGroup
              value={participantType}
              onValueChange={(value) => setParticipantType(value as ParticipantType)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ParticipantType.Host} id="type-host" />
                <Label htmlFor="type-host" className="font-normal cursor-pointer">
                  {t('participants:types.host')} - Full administrative access
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ParticipantType.Evaluator} id="type-evaluator" />
                <Label htmlFor="type-evaluator" className="font-normal cursor-pointer">
                  {t('participants:types.evaluator')} - Can score assigned criteria groups
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ParticipantType.Viewer} id="type-viewer" />
                <Label htmlFor="type-viewer" className="font-normal cursor-pointer">
                  {t('participants:types.viewer')} - Read-only access
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Criteria group assignment info - for future implementation */}
          {participantType === ParticipantType.Evaluator && criteriaGroups.length > 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                By default, this evaluator can score all criteria groups. You can change this after adding them.
              </AlertDescription>
            </Alert>
          )}

          {/* Form actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" className="flex-1">
              <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('participants:actions.inviteParticipant')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
